import { IResultStamp } from "@/api/AppApi/common-interfaces/IResultStamp";
import { isSignedInAtom } from "@/hooks/authAtoms";
import { useAtom } from "jotai";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { useEffect } from "react";
import { apiConfig } from "../../config";
import ApiAuth from "./api-auth";
import {
  IPostChangePassword,
  IPostRegister,
  IPostVerify,
} from "./interfaces/IAuth";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Keys } from "../../common-interfaces/quiries-keys/keysValues";
import { getKey } from "../../common-interfaces/quiries-keys/queries-keys";
import { getAccessToken } from "@/utils/functions/cookieOperations";

export const useAuth = () => new ApiAuth(apiConfig());

export const useRegisterPost = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IPostRegister) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.register, { onSuccess: onSuccess, onError: onError });
};

export const useSignIn = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IPostRegister) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.signin, { onSuccess: onSuccess, onError: onError });
};
export const useLogout = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.logout, { onSuccess: onSuccess, onError: onError });
};

export const useCheckAccountExist = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.checkAccountExist, {
    onSuccess: onSuccess,
    onError: onError,
  });
};

export const useOtpLogin = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IPostRegister) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.otpLogin, {
    onSuccess: onSuccess,
    onError: onError,
  });
};

export const useSocialLogin = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IPostRegister) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.socialLogin, {
    onSuccess: onSuccess,
    onError: onError,
  });
};

export const useSendVerificationCode = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IResultStamp) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.sendVerificationCode, {
    onSuccess: onSuccess,
    onError: onError,
  });
};

export const useVerifyPut = ({
  isEmail,
  onSuccess,
  onError,
}: {
  isEmail: boolean;
  onSuccess: (res: IPostVerify) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(isEmail ? auth.verifyEmail : auth.verifyMobile, {
    onSuccess: onSuccess,
    onError: onError,
  });
};

export const useForgetPassword = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IResultStamp) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.forgetPassword, {
    onSuccess: onSuccess,
    onError: onError,
  });
};

export const useVerifyForgetPassword = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IResultStamp) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.verifyForgetPassword, {
    onSuccess: onSuccess,
    onError: onError,
  });
};
export const useResetPassword = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IResultStamp) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.resetPassword, {
    onSuccess: onSuccess,
    onError: onError,
  });
};

export const useUpdatePassword = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IPostChangePassword) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.updatePassword, {
    onSuccess: onSuccess,
    onError: onError,
  });
};
export const useGetProfileQuery = ({
  redirectTo = "",
  redirectIfFound = false,
} = {}) => {
  const [isSignedIn] = useAtom(isSignedInAtom);
  const userRaw = useQuery(
    [...getKey(Keys.profileQueryKey), getAccessToken()],
    useAuth().getProfile,
    {
      // retry: (failureCount, error: any) => (error.statusCode === 401 && failureCount < 1 ? true : false),
      refetchOnWindowFocus: false,
      enabled: isSignedIn || !!getAccessToken(),
      // staleTime: Infinity,
    },
  );
  const user = userRaw.data?.result;
  const router = useRouter();
  useEffect(() => {
    if (!redirectTo || !user) return;

    if (
      // If redirectTo is set, redirect if the user was not found.
      (redirectTo && !redirectIfFound && !isSignedIn) ||
      // If redirectIfFound is also set, redirect if the user was found
      (redirectIfFound && isSignedIn)
    ) {
      router.push(redirectTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, redirectIfFound, redirectTo, isSignedIn]);

  return userRaw;
};

export const useUpdateProfile = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IResultStamp) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  return useMutation(auth.updateProfile, {
    onSuccess: onSuccess,
    onError: onError,
  });
};

export const useUpdateSettings = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IResultStamp) => void;
  onError: (x: any) => void;
}) => {
  const auth = useAuth();
  const getProfile = useGetProfileQuery();
  return useMutation(auth.updateSettings, {
    onSuccess: (res: IResultStamp) => {
      onSuccess(res);
      getProfile.refetch();
    },
    onError: onError,
  });
};